import { RealtimeDatabase as Database, RealtimeReference as Reference } from '../db/common';
import { ConsoleLogger, ILogger, NoOpLogger } from '../utils/logger';

/**
 * Abstract Database Service Class
 * All database services MUST extend this class and implement CRUD operations
 * 
 * This enforces a consistent interface across all services similar to Java abstract classes
 * 
 * @template T - The entity type this service manages
 * @template TCreate - Optional: The type used for creating entities (defaults to T)
 * @template TUpdate - Optional: The type used for updating entities (defaults to Partial<T>)
 */
abstract class DatabaseService<T, TCreate = T, TUpdate = Partial<T>> {
    protected logger: ILogger;

    constructor(
        protected db: Database,
        logger?: ILogger | boolean
    ) {
        // Use provided logger or default to NoOpLogger (does nothing)
        this.logger = !logger ?
            new NoOpLogger()
            : ((logger === true) || this.shouldLog()) ?
                new ConsoleLogger()
                : logger;
    }

    /**
     * Get the base path for this entity type in the database
     * Example: 'chatChannels', 'messages', 'users'
     */
    protected abstract getBasePath(): string;

    /**
     * Get a reference to the collection
     */
    protected getCollectionRef(): Reference {
        return this.db.ref(this.getBasePath());
    }

    /**
     * Get a reference to a specific entity by ID
     */
    protected getEntityRef(id: string): Reference {
        return this.getCollectionRef().child(id);
    }

    // ============================================
    // LOGGING HELPER METHODS
    // ============================================

    /**
     * Enable/disable logging for this service
     * Override to disable logging in production, etc.
     */
    protected shouldLog(): boolean {
        return true;
    }

    /**
     * Get the service name for logging
     * Override this to customize log messages
     */
    protected getServiceName(): string {
        return this.constructor.name;
    }

    /**
     * Log the start of an operation (debug level)
     * @param operation - The operation name (e.g., 'create', 'get', 'update')
     * @param id - The entity ID
     * @param data - Optional data being operated on
     */
    protected logOperation(operation: string, id: string, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation}`;
        this.logger.debug(message, {
            id,
            path: this.getBasePath(),
            data
        });
    }

    /**
     * Log successful completion of an operation
     * @param operation - The operation name
     * @param id - The entity ID
     * @param result - Optional result data
     */
    protected logSuccess(operation: string, id: string, result?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} succeeded`;
        this.logger.log(message, {
            id,
            path: this.getBasePath(),
            result
        });
    }

    /**
     * Log an error during an operation
     * @param operation - The operation name
     * @param id - The entity ID
     * @param error - The error that occurred
     * @param data - Optional data that was being operated on
     */
    protected logError(operation: string, id: string, error: Error, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} failed`;
        this.logger.error(message, {
            id,
            path: this.getBasePath(),
            error: error.message,
            stack: error.stack,
            data,
        });
    }

    // ============================================
    // ABSTRACT METHODS - MUST BE IMPLEMENTED
    // ============================================

    /**
     * Get an entity by ID
     * @param id - The entity ID
     * @returns The entity or null if not found
     */
    abstract get(id: string): Promise<T | null>;

    /**
     * Create a new entity
     * @param data - The data to create
     * @param id - Optional: Specific ID to use (otherwise auto-generated)
     * @returns The ID of the created entity
     */
    abstract create(data: TCreate, id?: string): Promise<string>;

    /**
     * Update an entity
     * @param id - The entity ID
     * @param data - The data to update
     */
    abstract update(id: string, data: TUpdate): Promise<void>;

    /**
     * Delete an entity
     * @param id - The entity ID
     */
    abstract delete(id: string): Promise<void>;

    // ============================================
    // OPTIONAL METHODS - CAN BE OVERRIDDEN
    // ============================================

    /**
     * List all entities (optional, can be overridden)
     * WARNING: Use with caution on large collections
     */
    async list(): Promise<Record<string, T>> {
        this.logOperation('list', 'all');

        try {
            const snapshot = await this.getCollectionRef().once('value');
            const result = snapshot.val() || {};

            this.logSuccess('list', 'all', { count: Object.keys(result).length });
            return result;
        } catch (error) {
            this.logError('list', 'all', error as Error);
            throw error;
        }
    }

    /**
     * Check if an entity exists
     */
    async exists(id: string): Promise<boolean> {
        this.logOperation('exists', id);

        try {
            const entity = await this.get(id);
            const result = entity !== null;

            this.logSuccess('exists', id, { exists: result });
            return result;
        } catch (error) {
            this.logError('exists', id, error as Error);
            throw error;
        }
    }

    /**
     * Batch create multiple entities
     */
    async batchCreate(entities: TCreate[]): Promise<string[]> {
        this.logOperation('batchCreate', 'batch', { count: entities.length });

        try {
            const ids = await Promise.all(
                entities.map(entity => this.create(entity))
            );

            this.logSuccess('batchCreate', 'batch', { count: ids.length, ids });
            return ids;
        } catch (error) {
            this.logError('batchCreate', 'batch', error as Error, { count: entities.length });
            throw error;
        }
    }

    /**
     * Batch delete multiple entities
     */
    async batchDelete(ids: string[]): Promise<void> {
        this.logOperation('batchDelete', 'batch', { count: ids.length, ids });

        try {
            await Promise.all(ids.map(id => this.delete(id)));
            this.logSuccess('batchDelete', 'batch', { count: ids.length });
        } catch (error) {
            this.logError('batchDelete', 'batch', error as Error, { count: ids.length, ids });
            throw error;
        }
    }
}

/**
 * Abstract Base Database Service Class
 * All database services MUST extend this class and implement CRUD operations
 * 
 * This enforces a consistent interface across all services similar to Java abstract classes
 * 
 * @template T - The entity type this service manages
 * @template TCreate - Optional: The type used for creating entities (defaults to T)
 * @template TUpdate - Optional: The type used for updating entities (defaults to Partial<T>)
 */
export abstract class BaseDatabaseService<T, TCreate = T, TUpdate = Partial<T>> extends DatabaseService<T, TCreate, TUpdate> {
    async get(id: string): Promise<T | null> {
        this.logOperation('get', id);

        try {
            const data = (await this.getEntityRef(id).get()).val();

            this.logSuccess('get', id, { found: data !== null });
            return data;
        } catch (error) {
            this.logError('get', id, error as Error);
            throw error;
        }
    }
    async create(data: TCreate, id?: string): Promise<string> {
        this.logOperation('create', id || 'auto', data);

        try {
            let entityId: string;

            if (!!id) {
                await this.getEntityRef(id).set(data);
                entityId = id;
            } else {
                const result = await this.getCollectionRef().push(data);
                entityId = result.key!;
            }

            this.logSuccess('create', entityId, { data });
            return entityId;
        } catch (error) {
            this.logError('create', id || 'auto', error as Error, data);
            throw error;
        }
    }
    async update(id: string, data: TUpdate): Promise<void> {
        this.logOperation('update', id, data);

        try {
            await this.getEntityRef(id).update(data as any);

            this.logSuccess('update', id, { data });
        } catch (error) {
            this.logError('update', id, error as Error, data);
            throw error;
        }
    }
    async delete(id: string): Promise<void> {
        this.logOperation('delete', id);

        try {
            await this.getEntityRef(id).remove();

            this.logSuccess('delete', id);
        } catch (error) {
            this.logError('delete', id, error as Error);
            throw error;
        }
    }
}

/**
 * Abstract Base Service Class for Two-Layer Data Structures
 * 
 * Use this for data that follows the pattern: parentId/childId
 * Examples:
 * - userChannels/{userId}/{channelId}
 * - chatMessages/{channelId}/{messageId}
 * 
 * @template T - The entity type this service manages
 * @template TCreate - Optional: The type used for creating entities (defaults to T)
 * @template TUpdate - Optional: The type used for updating entities (defaults to Partial<T>)
 */
export abstract class TwoLayerDatabaseService<T, TCreate = T, TUpdate = Partial<T>> {
    protected logger: ILogger;

    constructor(
        protected db: Database,
        logger?: ILogger | boolean
    ) {
        // Use provided logger or default to NoOpLogger (does nothing)
        this.logger = !logger ?
            new NoOpLogger()
            : ((typeof logger === 'boolean') || this.shouldLog()) ?
                new ConsoleLogger()
                : logger;
    }

    /**
     * Get the base path for this entity type (without IDs)
     * Example: 'userChatRooms' (the path will become 'userChatRooms/{parentId}/{childId}')
     */
    protected abstract getBasePath(): string;

    /**
     * Get reference to the entire collection (use with caution!)
     */
    protected getCollectionRef(): Reference {
        return this.db.ref(this.getBasePath());
    }

    /**
     * Get reference to all children under a parent
     * Example: userChatRooms/userId123
     */
    protected getParentRef(parentId: string): Reference {
        return this.getCollectionRef().child(parentId);
    }

    /**
     * Get reference to a specific child entity
     * Example: userChatRooms/userId123/channelId456
     */
    protected getChildRef(parentId: string, childId: string): Reference {
        return this.getParentRef(parentId).child(childId);
    }


    /**
     * Enable/disable logging for this service
     * Override to disable logging in production, etc.
     */
    protected shouldLog(): boolean {
        return true;
    }

    /**
     * Get the service name for logging
     * Override this to customize log messages
     */
    protected getServiceName(): string {
        return this.constructor.name;
    }

    // ============================================
    // LOGGING HELPERS
    // ============================================

    protected logOperation(operation: string, parentId: string, childId?: string, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation}`;
        this.logger.debug(message, {
            parentId,
            childId,
            path: this.getBasePath(),
            data,
        });
    }

    protected logSuccess(operation: string, parentId: string, childId?: string, result?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} succeeded`;
        this.logger.log(message, {
            parentId,
            childId,
            path: this.getBasePath(),
            result,
        });
    }

    protected logError(operation: string, parentId: string, error: Error, childId?: string, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} failed`;
        this.logger.error(message, {
            parentId,
            childId,
            path: this.getBasePath(),
            error: error.message,
            stack: error.stack,
            data,
        });
    }

    // ============================================
    // ABSTRACT METHODS - MUST BE IMPLEMENTED
    // ============================================

    /**
     * Get a child entity
     * @param parentId - The parent ID
     * @param childId - The child ID
     * @returns The entity or null if not found
     */
    abstract get(parentId: string, childId: string): Promise<T | null>;

    /**
     * Create a new child entity under a parent
     * @param parentId - The parent ID
     * @param data - The data to create
     * @param childId - Optional: Specific child ID to use (otherwise auto-generated)
     * @returns The child ID of the created entity
     */
    abstract create(parentId: string, data: TCreate, childId?: string): Promise<string>;

    /**
     * Update a child entity
     * @param parentId - The parent ID
     * @param childId - The child ID
     * @param data - The data to update
     */
    abstract update(parentId: string, childId: string, data: TUpdate): Promise<void>;

    /**
     * Delete a child entity
     * @param parentId - The parent ID
     * @param childId - The child ID
     */
    abstract delete(parentId: string, childId: string): Promise<void>;

    // ============================================
    // OPTIONAL METHODS - CAN BE OVERRIDDEN
    // ============================================

    /**
     * List all children under a parent
     * @param parentId - The parent ID
     * @returns Record of all children
     */
    async listByParent(parentId: string): Promise<Record<string, T>> {
        const snapshot = await this.getParentRef(parentId).get();
        return snapshot.val() || {};
    }

    /**
     * Check if a child entity exists
     * @param parentId - The parent ID
     * @param childId - The child ID
     */
    async exists(parentId: string, childId: string): Promise<boolean> {
        const entity = await this.get(parentId, childId);
        return entity !== null;
    }

    /**
     * Delete all children under a parent
     * @param parentId - The parent ID
     */
    async deleteAllByParent(parentId: string): Promise<void> {
        await this.getParentRef(parentId).remove();
    }

    /**
     * Count children under a parent
     * @param parentId - The parent ID
     */
    async countByParent(parentId: string): Promise<number> {
        const children = await this.listByParent(parentId);
        return Object.keys(children).length;
    }

    /**
     * Batch create multiple children under a parent
     * @param parentId - The parent ID
     * @param entities - Array of entities to create
     */
    async batchCreate(parentId: string, entities: TCreate[]): Promise<string[]> {
        const ids = await Promise.all(
            entities.map(entity => this.create(parentId, entity))
        );
        return ids;
    }

    /**
     * Batch delete multiple children
     * @param parentId - The parent ID
     * @param childIds - Array of child IDs to delete
     */
    async batchDelete(parentId: string, childIds: string[]): Promise<void> {
        await Promise.all(
            childIds.map(childId => this.delete(parentId, childId))
        );
    }

    /**
     * Get all parents that have children (WARNING: can be expensive!)
     */
    async getAllParents(): Promise<string[]> {
        const snapshot = await this.getCollectionRef().get();
        const data = snapshot.val() || {};
        return Object.keys(data);
    }

    /**
     * List all entities across all parents (WARNING: can be very expensive!)
     */
    async listAll(): Promise<Record<string, Record<string, T>>> {
        const snapshot = await this.getCollectionRef().get();
        return snapshot.val() || {};
    }
}

/**
 * Abstract Base Two Layers Database Service Class
 * All database services MUST extend this class and implement CRUD operations
 * 
 * This enforces a consistent interface across all services similar to Java abstract classes
 * 
 * @template T - The entity type this service manages
 * @template TCreate - Optional: The type used for creating entities (defaults to T)
 * @template TUpdate - Optional: The type used for updating entities (defaults to Partial<T>)
 */
export abstract class BaseTwoLayerDatabaseService<T, TCreate = T, TUpdate = Partial<T>> extends TwoLayerDatabaseService<T, TCreate, TUpdate> {
    async get(parentId: string, childId: string): Promise<T | null> {
        this.logOperation('get', parentId, childId);

        try {
            const result = await this.getChildRef(parentId, childId).get();
            const data = result.val();

            this.logSuccess('get', parentId, childId, { found: data !== null });
            return data;
        } catch (error) {
            this.logError('get', parentId, error as Error, childId);
            throw error;
        }
    }
    async create(parentId: string, data: TCreate, childId?: string): Promise<string> {
        this.logOperation('create', parentId, childId || 'auto', data);

        try {
            let entityId: string;

            if (childId) {
                await this.getChildRef(parentId, childId).set(data);
                entityId = childId;
            } else {
                const result = await this.getParentRef(parentId).push(data);
                entityId = result.key!;
            }

            this.logSuccess('create', parentId, entityId, { data });
            return entityId;
        } catch (error) {
            this.logError('create', parentId, error as Error, childId || 'auto', data);
            throw error;
        }
    }

    async update(parentId: string, childId: string, data: TUpdate): Promise<void> {
        this.logOperation('update', parentId, childId, data);

        try {
            await this.getChildRef(parentId, childId).update(data as any);
            this.logSuccess('update', parentId, childId, { data });
        } catch (error) {
            this.logError('update', parentId, error as Error, childId, data);
            throw error;
        }
    }

    async delete(parentId: string, childId: string): Promise<void> {
        this.logOperation('delete', parentId, childId);

        try {
            await this.getChildRef(parentId, childId).remove();
            this.logSuccess('delete', parentId, childId);
        } catch (error) {
            this.logError('delete', parentId, error as Error, childId);
            throw error;
        }
    }
}